import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../services/authentication/authContext';
import { useSubscription } from '../../hooks/useSubscription';
import { canAccessInventoryPage, isSuperAdminIdentity } from '../../services/accessManagement/accessManagement';
import { useImpersonationStore } from '../../stores/appImpersonationStore';
import { useAppUserDefaultStore } from '../../stores/appUserDefaultStore';
import { useRestaurantStore } from '../../stores/appRestaurantStore';
import { tourLocation, matchesTourRoute } from './tourLocation';
import { startInventoryPractice, endInventoryPractice, captureInventoryPracticeView } from './practice/inventoryPractice';
import { startUtilizationPractice, endUtilizationPractice } from './practice/utilizationPractice';
import { ONBOARDING_DESKTOP_QUERY } from './constants';
import { endPractice, startPractice, ensurePracticeRestaurant } from './practice/practiceSession';
import { OnboardingOverlay } from './OnboardingOverlay';
import { useOnboardingStore } from './onboardingStore';
import { prepareDataTour } from './practice/prepareDataTour';
import { prepareInventoryTourStep } from './tours/inventory';
import { tourLaunchIdentity, usePendingTourStore } from './pendingTourStore';
import { canAccessTour, getTour, getTourForPath, tourOwnsPath } from './tours';

/** One app mount; page definitions and data-onboarding anchors own each tour. */
export function OnboardingRoot() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, backendUser, accountSettings, loading } = useAuth();
  const { isActive } = useSubscription();
  const impersonation = useImpersonationStore(s => s.target);
  const [desktop, setDesktop] = useState(() => window.matchMedia(ONBOARDING_DESKTOP_QUERY).matches);
  const state = useOnboardingStore();
  const pendingLaunch = usePendingTourStore(s => s.pending);
  const clearPendingLaunch = usePendingTourStore(s => s.clear);
  const pendingDestinationSeen = useRef<typeof pendingLaunch>(null);
  const { hydrate, reset, dismiss, maybeStartForPath, startTour, next, back, skip, restart, startBranch } = state;
  const expectedUserId = impersonation?.userId ?? user?.id;
  const identityReady = Boolean(user && backendUser && backendUser.id === expectedUserId);
  const currentLocation = tourLocation(location.pathname, location.search);
  const pageTour = getTourForPath(location.pathname, { includeSubpages: true, search: location.search });
  const tour = state.tourId ? getTour(state.tourId) : undefined;
  const accessTour = state.running && tour && tourOwnsPath(tour, currentLocation) ? tour : pageTour;
  const eligible = !loading && identityReady && isActive && Boolean(accessTour && canAccessTour(accessTour, backendUser, accountSettings));
  const step = state.running && tour ? (state.branchSteps ?? tour.steps)[state.stepIndex] : null;
  const visible = desktop && eligible && state.userId === expectedUserId && state.impersonating === Boolean(impersonation) && Boolean(tour && tourOwnsPath(tour, currentLocation));
  const routeRef = useRef(currentLocation);
  const navigateRef = useRef(navigate);
  const expectedRoute = useRef<string | null>(null);
  const lastVisitedRoute = useRef(currentLocation);
  routeRef.current = currentLocation;
  navigateRef.current = navigate;

  useEffect(() => {
    const query = window.matchMedia(ONBOARDING_DESKTOP_QUERY);
    const onChange = () => setDesktop(query.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!identityReady || !backendUser) {
      reset();
      return;
    }
    void hydrate(backendUser.id, Boolean(impersonation));
  }, [loading, identityReady, backendUser, impersonation, hydrate, reset]);

  useEffect(() => {
    if (pendingLaunch) {
      // An explicit replay owns this navigation, including the hydration window.
      const identity = tourLaunchIdentity(user?.id, expectedUserId, Boolean(impersonation));
      const requestedTour = getTour(pendingLaunch.tourId);
      if (pendingLaunch.identity !== identity || !desktop || (!loading && (!identityReady || !isActive || !requestedTour || !canAccessTour(requestedTour, backendUser, accountSettings))) || state.hydrationError) {
        clearPendingLaunch();
        dismiss();
        return;
      }
      if (location.pathname === pendingLaunch.fromPath && location.key === pendingLaunch.fromKey && pendingDestinationSeen.current !== pendingLaunch) return;
      if (!matchesTourRoute(currentLocation, pendingLaunch.destination)) {
        clearPendingLaunch();
        dismiss();
        return;
      }
      pendingDestinationSeen.current = pendingLaunch;
      if (!loading && eligible && state.hydrated && state.userId === expectedUserId && state.impersonating === Boolean(impersonation)) {
        clearPendingLaunch();
        startTour(pendingLaunch.tourId);
      }
      return;
    }
    if (!desktop || !eligible) {
      dismiss();
      return;
    }
    // Super admins can launch tours explicitly, but never get automatic introductions.
    if (state.hydrated && !isSuperAdminIdentity(backendUser)) maybeStartForPath(currentLocation);
  }, [desktop, eligible, state.hydrated, state.hydrationError, state.userId, state.impersonating, startTour, currentLocation, location.pathname, location.key, dismiss, maybeStartForPath, pendingLaunch, clearPendingLaunch, user?.id, expectedUserId, impersonation, loading, identityReady, isActive, backendUser, accountSettings]);

  useEffect(() => {
    if (!pendingLaunch) return;
    const timeout = window.setTimeout(clearPendingLaunch, 15000);
    return () => window.clearTimeout(timeout);
  }, [pendingLaunch, clearPendingLaunch]);

  // Each domain owns its disposable dataset and view snapshot. Step changes keep it
  // alive; replay resets it, while a route/identity/restaurant change tears it down.
  const restaurantId = useAppUserDefaultStore(s => s.restaurant?.id);
  const practice = visible ? (step?.practice === 'subrecipe' ? 'ingredient' : step?.practice) : undefined;
  const restaurantsReady = useRestaurantStore(s => s.isDataLoaded);
  const isDataTour = practice === 'inventory' || practice === 'leakage' || practice === 'utilization';
  const [readyPractice, setReadyPractice] = useState<string | null>(null);
  const [availableStep, setAvailableStep] = useState<string | null>(null);
  const activePractice = useRef<{ controller?: AbortController; kind: NonNullable<typeof practice>; session: number; restaurant?: string; originalRestaurant?: ReturnType<typeof useAppUserDefaultStore.getState>['restaurant'] } | null>(null);
  const stopPractice = () => {
    const previous = activePractice.current;
    activePractice.current = null;
    if (!previous) return;
    previous.controller?.abort();
    if (previous.kind === 'inventory' || previous.kind === 'leakage') endInventoryPractice();
    else if (previous.kind === 'utilization') endUtilizationPractice();
    else endPractice();
    if (previous.originalRestaurant !== undefined && useAppUserDefaultStore.getState().restaurant?.id === previous.restaurant) {
      useAppUserDefaultStore.setState({ restaurant: previous.originalRestaurant });
    }
  };
  const stopPracticeRef = useRef(stopPractice);
  stopPracticeRef.current = stopPractice;
  useEffect(() => {
    if (!isDataTour || restaurantsReady || useRestaurantStore.getState().isLoading) return;
    let cancelled = false;
    void useRestaurantStore.getState().loadAllData().catch(() => { if (!cancelled) dismiss(); });
    return () => { cancelled = true; };
  }, [isDataTour, restaurantsReady, dismiss]);
  useLayoutEffect(() => {
    const previous = activePractice.current;
    if (!practice) { stopPracticeRef.current(); return; }
    if (previous?.session === state.sessionId && previous.restaurant !== restaurantId) {
      stopPracticeRef.current();
      dismiss();
      if (location.pathname.startsWith('/inventory/section/onboarding-practice-')) navigate('/inventory?tab=review');
      return;
    }
    if (previous?.kind === practice && previous.session === state.sessionId) return;
    if (isDataTour && !restaurantsReady) return;
    const family = (kind: string) => ['inventory', 'leakage'].includes(kind) ? 'inventory'
      : kind === 'utilization' ? kind : 'costPrice';
    if (previous && family(previous.kind) !== family(practice)) stopPracticeRef.current();
    if (previous && previous.session !== state.sessionId && family(practice) !== 'costPrice') stopPracticeRef.current();
    const originalRestaurant = previous?.originalRestaurant !== undefined ? previous.originalRestaurant : useAppUserDefaultStore.getState().restaurant;
    if (practice === 'inventory' || practice === 'leakage' || practice === 'utilization') {
      const selectedId = ensurePracticeRestaurant();
      if (!selectedId) { dismiss(); return; }
      const controller = new AbortController();
      const pending = { kind: practice, session: state.sessionId, restaurant: selectedId, originalRestaurant, controller };
      activePractice.current = pending;
      setReadyPractice(null);
      if (practice !== 'utilization') captureInventoryPracticeView();
      void prepareDataTour(practice, selectedId, canAccessInventoryPage(backendUser, accountSettings), controller.signal, backendUser).then(prepared => {
        if (controller.signal.aborted || activePractice.current !== pending) return;
        if (!prepared || useAppUserDefaultStore.getState().restaurant?.id !== selectedId) {
          stopPracticeRef.current(); dismiss(); return;
        }
        const started = practice === 'utilization' ? startUtilizationPractice(prepared) : startInventoryPractice(practice, prepared);
        if (!started) { stopPracticeRef.current(); dismiss(); return; }
        activePractice.current = pending;
        setReadyPractice(`${state.sessionId}:${practice}:${selectedId}`);
      });
      return;
    }
    const started = startPractice(practice === 'overview', true);
    if (!started) { stopPracticeRef.current(); dismiss(); return; }
    activePractice.current = { kind: practice, session: state.sessionId, restaurant: useAppUserDefaultStore.getState().restaurant?.id };
    setReadyPractice(`${state.sessionId}:${practice}:${useAppUserDefaultStore.getState().restaurant?.id}`);
  }, [practice, isDataTour, restaurantsReady, state.sessionId, restaurantId, dismiss, location.pathname, navigate, backendUser, accountSettings]);
  useLayoutEffect(() => () => { stopPracticeRef.current(); }, []);

  const practiceReady = !practice || readyPractice === `${state.sessionId}:${practice}:${restaurantId}`;
  const stepKey = step ? `${state.sessionId}:${step.id}` : null;
  const routeReady = !step?.route || matchesTourRoute(currentLocation, step.route);
  const resolvedStep = useRef<{ session: number; tourId: string | null; branch: typeof state.branchSteps; index: number } | null>(null);
  useLayoutEffect(() => {
    if (!visible || !practiceReady || !step) return;
    const previous = resolvedStep.current;
    const goingBack = previous?.session === state.sessionId && previous.tourId === state.tourId
      && previous.branch === state.branchSteps && state.stepIndex < previous.index;
    // Restore the customer view before checking a reading step's target on Back.
    if (practice === 'inventory' || practice === 'leakage') prepareInventoryTourStep(step.id);
    if (step.isAvailable && !step.isAvailable()) {
      if (goingBack) back();
      else next();
      return;
    }
    resolvedStep.current = { session: state.sessionId, tourId: state.tourId, branch: state.branchSteps, index: state.stepIndex };
    setAvailableStep(stepKey);
  }, [step, stepKey, visible, practiceReady, practice, next, back, state.sessionId, state.tourId, state.branchSteps, state.stepIndex]);

  // Skipped steps must not navigate to an unavailable target's route.
  useEffect(() => {
    if (!visible || !practiceReady || !step || availableStep !== stepKey) return;
    expectedRoute.current = step.route ?? routeRef.current;
    if (!matchesTourRoute(routeRef.current, expectedRoute.current)) navigateRef.current(expectedRoute.current);
  }, [step, visible, practiceReady, availableStep, stepKey, state.sessionId]);

  useEffect(() => {
    const previousPath = lastVisitedRoute.current;
    lastVisitedRoute.current = currentLocation;
    if (previousPath !== currentLocation && state.running && expectedRoute.current && !matchesTourRoute(currentLocation, expectedRoute.current)
      && (!step?.route || !matchesTourRoute(currentLocation, step.route))) dismiss();
  }, [currentLocation, state.running, step, dismiss]);

  useLayoutEffect(() => {
    if (!visible || !practiceReady || !routeReady || !step || availableStep !== stepKey) return;
    return step.onEnter?.();
  }, [step, visible, practiceReady, routeReady, availableStep, stepKey, state.sessionId]);

  // Restore the initiating control only when the whole tour closes, not between steps.
  useEffect(() => {
    if (!state.running || !visible) return;
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    return () => {
      if (previous?.isConnected) previous.focus({ preventScroll: true });
    };
  }, [state.running, visible]);

  if (!visible || !practiceReady || !routeReady || !step || availableStep !== stepKey) return null;
  const finishAndReturn = (complete: boolean) => {
    if (complete) void state.complete();
    else skip();
    // Disposable section routes cannot outlive the dataset that supplies them.
    if (location.pathname.startsWith('/inventory/section/onboarding-practice-')) navigate('/inventory?tab=review');
  };
  const onPrimary = () => {
    if (step.primaryTour) {
      // Finish this tour independently before opening the next one. Persistence
      // may finish later; the store records completion synchronously.
      void state.complete();
      const destination = getTour(step.primaryTour);
      if (destination && canAccessTour(destination, backendUser, accountSettings)) {
        navigate(step.primaryRoute ?? destination.entryRoute ?? destination.paths[0]);
        startTour(destination.id);
      }
      return;
    }
    next();
    if (step.primaryRoute) navigate(step.primaryRoute);
  };
  return <>
    <OnboardingOverlay
      key={`${state.sessionId}:${step.id}`}
      step={step}
      actionCompleted={state.completedStepIds.includes(step.id)}
      onPrimary={onPrimary}
      onBack={state.stepIndex > 0 || state.branchSteps ? back : undefined}
      onSecondary={step.secondaryAction === 'restart' ? restart : () => finishAndReturn(step.secondaryAction === 'complete')}
      onComplete={() => finishAndReturn(true)}
      onTaskStart={startBranch}
    />
  </>;
}
